import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Instagram, MapPin, Phone } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      setError("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen pt-24 md:pt-32" data-testid="contact-page">
      {/* Header */}
      <section className="section-padding pb-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              Get in Touch
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white mb-6">
              Let's Start a <span className="italic">Conversation</span>
            </h1>
            <p className="text-[#A1A1A1] text-base md:text-lg leading-relaxed">
              Ready to transform your digital presence? We'd love to hear from you.
              Reach out and let's discuss how we can bring your vision to life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding pt-0">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {submitted ? (
                <div className="bg-[#0F0F0F] p-12 border border-[#C5A059]/30" data-testid="success-message">
                  <h3 className="font-heading text-2xl text-white mb-4">
                    Thank you!
                  </h3>
                  <p className="text-[#A1A1A1] mb-6">
                    Your message has been received. We'll get back to you shortly.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#C5A059] text-sm uppercase tracking-widest hover:underline"
                    data-testid="send-another-button"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div>
                    <label className="block text-[#A1A1A1] text-xs uppercase tracking-widest mb-4">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-line"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#A1A1A1] text-xs uppercase tracking-widest mb-4">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-line"
                      placeholder="john@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#A1A1A1] text-xs uppercase tracking-widest mb-4">
                      Your Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="input-line resize-none"
                      placeholder="Tell us about your project..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  {error && (
                    <p className="text-red-400 text-sm" data-testid="error-message">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto flex items-center justify-center gap-3"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                    <ArrowUpRight size={16} />
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="space-y-12"
            >
              {/* WhatsApp CTA */}
              <div className="bg-[#0F0F0F] p-8 md:p-12 border border-white/5">
                <h3 className="font-heading text-2xl text-white mb-4">
                  Prefer WhatsApp?
                </h3>
                <p className="text-[#A1A1A1] mb-6">
                  Get a faster response by chatting with us directly on WhatsApp.
                </p>
                <a
                  href="https://wa.me/918006521008"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 hover:bg-[#20bd5a] transition-colors"
                  data-testid="contact-whatsapp-button"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-5 h-5"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Chat on WhatsApp
                </a>
              </div>

              {/* Contact Details */}
              <div className="space-y-8">
                <div>
                  <h4 className="text-white text-xs uppercase tracking-[0.2em] mb-4">
                    Email
                  </h4>
                  <a
                    href="mailto:aniket@aakaardesigns.com"
                    className="text-[#A1A1A1] hover:text-[#C5A059] transition-colors flex items-center gap-3"
                    data-testid="contact-email-link"
                  >
                    <Mail size={18} />
                    aniket@aakaardesigns.com
                  </a>
                </div>

                <div>
                  <h4 className="text-white text-xs uppercase tracking-[0.2em] mb-4">
                    Phone
                  </h4>
                  <a
                    href="tel:+918006521008"
                    className="text-[#A1A1A1] hover:text-[#C5A059] transition-colors flex items-center gap-3"
                    data-testid="contact-phone-link"
                  >
                    <Phone size={18} />
                    +91 800 652 1008
                  </a>
                </div>

                <div>
                  <h4 className="text-white text-xs uppercase tracking-[0.2em] mb-4">
                    Social
                  </h4>
                  <a
                    href="https://instagram.com/aakaardesigns"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#A1A1A1] hover:text-[#C5A059] transition-colors flex items-center gap-3"
                    data-testid="contact-instagram-link"
                  >
                    <Instagram size={18} />
                    @aakaardesigns
                  </a>
                </div>

                <div>
                  <h4 className="text-white text-xs uppercase tracking-[0.2em] mb-4">
                    Location
                  </h4>
                  <p className="text-[#A1A1A1] flex items-center gap-3">
                    <MapPin size={18} />
                    India
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map or Additional Section */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0F0F0F] p-12 md:p-16 text-center border border-white/5"
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              Response Time
            </p>
            <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
              We typically respond within 24 hours
            </h3>
            <p className="text-[#A1A1A1] max-w-xl mx-auto">
              For urgent inquiries, WhatsApp is the fastest way to reach us.
              We're available Monday through Saturday, 10 AM to 7 PM IST.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
